// 정기모임 템플릿 시트 묶음 — 운영진 전용.
//
// 새 일정 시트(`openNewEventSheet`)에서 쓰는 템플릿 픽커는 여기서
// 만들어진 후 콜백으로 주입된다. 템플릿 폼/리스트/저장 프롬프트는
// 모두 운영진 전용이라 admin 분기는 호출 측에서 거른다.

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { Icon } from "../ui/components/Icon";
import { buildSurfaceSelect, courtLetter, DAY_LABELS } from "../ui/format";
import {
  buildMiniTimePicker,
  buildNumberStepper,
  buildSegment,
  buildTimePicker,
} from "../ui/forms";
import { DomNode, mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";
import { buildScheduleSummary, formatTemplateSummary } from "./index";

export async function fetchEventTemplates(): Promise<any[]> {
  try {
    const res = await api("/api/v1/admin/event-templates");
    return (res && res.templates) || [];
  } catch (e: any) {
    if (e.status === 404) return [];
    throw e;
  }
}

function TemplatePicker({ applyFn }: { applyFn: (t: any) => void }) {
  const [templates, setTemplates] = useState<any[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEventTemplates()
      .then((list) => { if (!cancelled) setTemplates(list); })
      .catch(() => { if (!cancelled) setTemplates([]); });
    return () => { cancelled = true; };
  }, []);

  if (!templates || templates.length === 0) return null;

  return (
    <div class="field template-picker">
      <span class="field__label">템플릿에서 시작 (선택)</span>
      <div class="template-picker__list">
        {templates.map((t) => {
          const summary = formatTemplateSummary(t);
          const active = activeId === t.template_id;
          return (
            <button
              key={t.template_id}
              type="button"
              class={"template-chip" + (active ? " template-chip--active" : "")}
              onClick={() => {
                applyFn(t);
                toast(`"${t.name}"\n템플릿 적용됨`);
                setActiveId(t.template_id);
              }}
            >
              <span class="template-chip__name">{t.name}</span>
              {summary && <span class="template-chip__meta">{summary}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function buildTemplatePickerSection(applyFn: (t: any) => void): HTMLElement {
  return mountPreact(<TemplatePicker applyFn={applyFn} />);
}

function TemplatesListSheet() {
  const [templates, setTemplates] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    setError(null);
    setTemplates(null);
    try {
      setTemplates(await fetchEventTemplates());
    } catch (e: any) {
      setError(e.message || "템플릿을 불러오지 못했어요");
      setTemplates([]);
    }
  }

  useEffect(() => { refresh(); }, []);

  return (
    <div class="stack">
      <div class="stack">
        {templates === null && !error && <div class="loading">불러오는 중…</div>}
        {error && <div class="error-card">{error}</div>}
        {templates && templates.length === 0 && !error && (
          <div class="card empty">
            <div class="empty__big">📅</div>
            <div>아직 템플릿이 없어요</div>
            <div class="muted small">매주 반복되는 정기모임 설정을 저장해두면 편해요</div>
          </div>
        )}
        {templates && templates.length > 0 && (
          <div class="list list--compact">
            {templates.map((t) => {
              const summary = formatTemplateSummary(t);
              return (
                <button
                  key={t.template_id}
                  type="button"
                  class="list__item"
                  onClick={() => openEventTemplateFormSheet(t, refresh, openEventTemplatesSheet)}
                >
                  <div class="list__main">
                    <div class="list__title">{t.name}</div>
                    {summary && <div class="list__sub">{summary}</div>}
                  </div>
                  <div class="list__trail"><Icon name="chevron" size={16} /></div>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <button
        type="button"
        class="btn btn--primary btn--block"
        onClick={() => openEventTemplateFormSheet(null, refresh, openEventTemplatesSheet)}
      >
        + 새 템플릿 만들기
      </button>
    </div>
  );
}

export function openEventTemplatesSheet(): void {
  openSheet("정기모임 템플릿", mountPreact(<TemplatesListSheet />));
}

export function openSaveAsTemplatePromptSheet(preset: any): void {
  const body = mountPreact(
    <div class="stack">
      <p>이 세팅을 템플릿으로 저장할까요?</p>
      <p class="muted">
        다음에 같은 일정 만들 때 <strong>1번 클릭으로 바로 불러올 수 있어요.</strong>
      </p>
      <button
        type="button"
        class="btn btn--primary btn--block"
        onClick={() => {
          closeSheet();
          openEventTemplateFormSheet(preset, () => toast("템플릿을 저장했어요"));
        }}
      >
        템플릿으로 저장
      </button>
      <button
        type="button"
        class="btn btn--ghost btn--block"
        onClick={() => closeSheet()}
      >
        나중에
      </button>
    </div>,
  );
  openSheet("템플릿으로 저장", body);
}

interface CourtEntry {
  label: string;
  fromTime: string | null;
  untilTime: string | null;
  surface: string | null;
}

interface CourtRowProps {
  entry: CourtEntry;
  index: number;
  total: number;
  startTime: ReturnType<typeof buildTimePicker>;
  endTime: ReturnType<typeof buildTimePicker>;
  onUpdate: (patch: Partial<CourtEntry>) => void;
  onRemove: () => void;
}

function CourtRow({ entry, index, total, startTime, endTime, onUpdate, onRemove }: CourtRowProps) {
  const [windowOpen, setWindowOpen] = useState(!!(entry.fromTime || entry.untilTime));
  const surfaceSel = useMemo(() => buildSurfaceSelect(entry.surface, (v) => onUpdate({ surface: v })), []);
  const fromSel = useMemo(() => buildMiniTimePicker(entry.fromTime, (v) => onUpdate({ fromTime: v })), []);
  const untilSel = useMemo(() => buildMiniTimePicker(entry.untilTime, (v) => onUpdate({ untilTime: v })), []);

  function openWindow() {
    setWindowOpen(true);
    if (!entry.fromTime) {
      const v = startTime.getValue();
      fromSel.setValue(v);
      onUpdate({ fromTime: v });
    }
    if (!entry.untilTime) {
      const v = endTime.getValue();
      untilSel.setValue(v);
      onUpdate({ untilTime: v });
    }
  }

  function clearWindow() {
    setWindowOpen(false);
    onUpdate({ fromTime: null, untilTime: null });
  }

  return (
    <div class="court-row court-row--segment">
      <div class="court-row__top">
        <input
          class="input input--compact"
          type="text"
          value={entry.label}
          placeholder={courtLetter(index)}
          onInput={(e) => onUpdate({ label: (e.currentTarget as HTMLInputElement).value })}
        />
        <DomNode node={surfaceSel} />
        {!windowOpen && (
          <button
            type="button"
            class="court-row__clock"
            aria-label="시간대 설정"
            onClick={openWindow}
          >
            ⏱
          </button>
        )}
        {total > 1 && (
          <button
            type="button"
            class="court-row__remove"
            aria-label="삭제"
            onClick={onRemove}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
      <div class="court-row__window" hidden={!windowOpen}>
        <span class="court-row__window-label">시간대</span>
        <DomNode node={fromSel.el} />
        <span class="court-row__window-sep">–</span>
        <DomNode node={untilSel.el} />
        <button
          type="button"
          class="court-row__remove"
          aria-label="시간대 해제"
          onClick={clearWindow}
        >
          <Icon name="x" size={12} />
        </button>
      </div>
    </div>
  );
}

function TemplateForm({ existing, onSaved }: { existing: any; onSaved?: () => void }) {
  const isEdit = !!(existing && existing.template_id);
  const t = existing || {};

  const [name, setName] = useState<string>(t.name || "");
  const [titlePattern, setTitlePattern] = useState<string>(t.title_pattern || "");
  const [location, setLocation] = useState<string>(t.location || "");
  const [notes, setNotes] = useState<string>(t.notes || "");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const initialEntries: CourtEntry[] = (t.courts && t.courts.length > 0)
    ? t.courts.map((c: any) => ({
        label: c.court_label || "",
        fromTime: c.available_from || null,
        untilTime: c.available_until || null,
        surface: c.surface_type || null,
      }))
    : Array.from({ length: t.court_count || 4 }, () => ({ label: "", fromTime: null, untilTime: null, surface: null }));
  const [courts, setCourts] = useState<CourtEntry[]>(initialEntries);

  const dowHidden = useMemo(() => {
    const initVal = (t.day_of_week !== null && t.day_of_week !== undefined) ? t.day_of_week : 3;
    const inp = document.createElement("input");
    inp.type = "hidden";
    inp.value = String(initVal);
    return inp;
  }, []);
  const dowSeg = useMemo(
    () => buildSegment(
      DAY_LABELS.map((lbl, i) => [String(i), lbl] as [string, string]),
      dowHidden.value,
      (val: string) => { dowHidden.value = val; },
    ),
    [],
  );

  const startTime = useMemo(() => {
    const [sHH, sMM] = (t.start_time || "21:00").split(":");
    return buildTimePicker(sHH === "24" ? "00" : sHH, sMM);
  }, []);
  const endTime = useMemo(() => {
    const [eHH, eMM] = (t.end_time || "00:00").split(":");
    return buildTimePicker(eHH === "24" ? "00" : eHH, eMM);
  }, []);

  const modeHidden = useMemo(() => {
    const inp = document.createElement("input");
    inp.type = "hidden";
    inp.value = t.mode || "kdk";
    return inp;
  }, []);
  const modeSeg = useMemo(
    () => buildSegment(
      [["kdk", "개인전/KDK"], ["team", "팀전"]],
      modeHidden.value,
      (val: string) => { modeHidden.value = val; },
    ),
    [],
  );

  const roundsMin = useMemo(() => buildNumberStepper(t.max_round_count || 6, 1, 30, 1, "라운드"), []);
  const matchMin = useMemo(() => buildNumberStepper(t.match_duration_minutes || 30, 10, 90, 1, "분"), []);
  const breakMin = useMemo(() => buildNumberStepper(t.break_duration_minutes ?? 5, 0, 30, 1, "분"), []);
  const warmupMin = useMemo(() => buildNumberStepper(t.warmup_duration_minutes ?? 10, 0, 30, 1, "분"), []);

  const tplMatchManuallySet = useRef(false);
  useEffect(() => {
    matchMin.onUserChange(() => { tplMatchManuallySet.current = true; });
    function recomputeTplMatch() {
      if (tplMatchManuallySet.current) return;
      const [sh, sm] = startTime.getValue().split(":").map(Number);
      const [eh, em] = endTime.getValue().split(":").map(Number);
      let total = (eh * 60 + em) - (sh * 60 + sm);
      if (total <= 0) total += 24 * 60;
      const r = roundsMin.getValue();
      const w = warmupMin.getValue();
      const b = breakMin.getValue();
      if (r < 1) return;
      const raw = (total - w + b) / r - b;
      const stepped = Math.max(10, Math.min(90, Math.floor(raw)));
      matchMin.setValue(stepped);
    }
    startTime.onChange(recomputeTplMatch);
    endTime.onChange(recomputeTplMatch);
    roundsMin.onChange(recomputeTplMatch);
    warmupMin.onChange(recomputeTplMatch);
    breakMin.onChange(recomputeTplMatch);
    if (!isEdit) recomputeTplMatch();
  }, []);

  const scheduleSummary = useMemo(
    () => buildScheduleSummary(startTime, endTime, roundsMin, matchMin, breakMin, warmupMin),
    [],
  );

  const submitRef = useRef<HTMLButtonElement>(null);

  function getTplCourts() {
    return courts.map((e, i) => {
      const out: any = { court_label: e.label.trim() || courtLetter(i) };
      if (e.surface) out.surface_type = e.surface;
      if (e.fromTime) out.available_from = e.fromTime;
      if (e.untilTime) out.available_until = e.untilTime;
      return out;
    });
  }

  function updateCourt(idx: number, patch: Partial<CourtEntry>) {
    setCourts((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  }

  function removeCourt(idx: number) {
    setCourts((prev) => prev.filter((_, i) => i !== idx));
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (!name.trim()) { toast("템플릿 이름을 입력해주세요"); return; }
    const courtsPayload = getTplCourts();
    const body: any = {
      name: name.trim(),
      title_pattern: titlePattern.trim() || null,
      day_of_week: Number(dowHidden.value),
      start_time: startTime.getValue(),
      end_time: endTime.getValue(),
      location: location.trim() || null,
      mode: modeHidden.value,
      event_type: "regular",
      court_count: courtsPayload.length,
      courts: courtsPayload,
      max_round_count: roundsMin.getValue(),
      match_duration_minutes: matchMin.getValue(),
      break_duration_minutes: breakMin.getValue(),
      warmup_duration_minutes: warmupMin.getValue(),
      notes: notes.trim() || null,
    };
    try {
      await withBusy(submitRef.current, "저장 중…", async () => {
        if (isEdit) {
          await api(`/api/v1/admin/event-templates/${t.template_id}`, { method: "PATCH", body });
        } else {
          await api("/api/v1/admin/event-templates", { method: "POST", body });
        }
        toast(isEdit ? "저장했어요" : "템플릿을 만들었어요");
        closeSheet();
        if (onSaved) onSaved();
      });
    } catch (err: any) { toast(err.message); }
  }

  async function onDelete() {
    if (!confirm(`"${t.name}" 템플릿을 삭제할까요?`)) return;
    try {
      await api(`/api/v1/admin/event-templates/${t.template_id}`, { method: "DELETE" });
      toast("삭제했어요");
      closeSheet();
      if (onSaved) onSaved();
    } catch (err: any) { toast(err.message); }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="field">
        <span class="field__label">템플릿 이름</span>
        <input
          class="input"
          type="text"
          placeholder="예: 하이볼 수요일"
          value={name}
          required
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">일정명 (기본값)</span>
        <input
          class="input"
          type="text"
          placeholder="예: 수요 정기모임"
          value={titlePattern}
          onInput={(e) => setTitlePattern((e.currentTarget as HTMLInputElement).value)}
        />
        <span class="field__hint">새 일정 만들 때 자동으로 채워지는 이름</span>
      </div>
      <div class="field">
        <span class="field__label">요일</span>
        <DomNode node={dowHidden} />
        <DomNode node={dowSeg} />
      </div>
      <div class="field-row">
        <div class="field field--half">
          <span class="field__label">시작 시간</span>
          <DomNode node={startTime.el} />
        </div>
        <div class="field field--half">
          <span class="field__label">종료 시간</span>
          <DomNode node={endTime.el} />
        </div>
      </div>
      <div class="field">
        <span class="field__label">장소 (선택)</span>
        <input
          class="input"
          type="text"
          placeholder="예: 하이볼 테니스장"
          value={location}
          onInput={(e) => setLocation((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">유형</span>
        <DomNode node={modeHidden} />
        <DomNode node={modeSeg} />
      </div>
      <div class="field">
        <span class="field__label">코트</span>
        <div class="court-inputs">
          {courts.map((c, i) => (
            <CourtRow
              key={i}
              entry={c}
              index={i}
              total={courts.length}
              startTime={startTime}
              endTime={endTime}
              onUpdate={(patch) => updateCourt(i, patch)}
              onRemove={() => removeCourt(i)}
            />
          ))}
        </div>
        <button
          type="button"
          class="btn btn--ghost btn--sm"
          onClick={() => setCourts((prev) => [...prev, { label: "", fromTime: null, untilTime: null, surface: null }])}
        >
          + 코트 추가
        </button>
      </div>
      <div class="field">
        <span class="field__label">라운드 수</span>
        <DomNode node={roundsMin.el} />
      </div>
      <button
        type="button"
        class="btn btn--ghost btn--sm"
        onClick={() => setAdvancedOpen((v) => !v)}
      >
        {advancedOpen ? "▾ 상세 설정" : "▸ 상세 설정"}
      </button>
      <div class="stack" hidden={!advancedOpen}>
        <div class="field-row field-row--dense">
          <div class="field field--half">
            <span class="field__label">웜업</span>
            <DomNode node={warmupMin.el} />
          </div>
          <div class="field field--half">
            <span class="field__label">경기</span>
            <DomNode node={matchMin.el} />
          </div>
          <div class="field field--half">
            <span class="field__label">휴게</span>
            <DomNode node={breakMin.el} />
          </div>
        </div>
        <span class="field__hint">웜업: 일정 시작 후 첫 경기 전 몸풀기 시간</span>
      </div>
      <DomNode node={scheduleSummary} />
      <div class="field">
        <span class="field__label">메모 (선택)</span>
        <textarea
          class="input"
          rows={2}
          placeholder="메모 (선택)"
          value={notes}
          onInput={(e) => setNotes((e.currentTarget as HTMLTextAreaElement).value)}
        />
      </div>
      <button ref={submitRef} class="btn btn--primary btn--block" type="submit">
        {isEdit ? "저장" : "템플릿 만들기"}
      </button>
      {isEdit && (
        <button type="button" class="btn btn--danger btn--block" onClick={onDelete}>삭제</button>
      )}
    </form>
  );
}

export function openEventTemplateFormSheet(
  existing: any,
  onSaved?: () => void,
  onClose?: () => void,
): void {
  const isEdit = !!(existing && existing.template_id);
  openSheet(
    isEdit ? "템플릿 편집" : "새 템플릿",
    mountPreact(<TemplateForm existing={existing} onSaved={onSaved} />),
    null,
    { onClose },
  );
}
